import React, { useState, useEffect, useMemo, useRef } from "react";
import axios from "axios";
import { server } from "../../server";
import useAuth from "../../useAuth";
import {
  FaUserGraduate,
  FaSearch,
  FaFileInvoiceDollar,
  FaReceipt,
  FaCheckCircle,
  FaClock,
  FaExclamationTriangle,
  FaHistory,
  FaBell,
  FaPrint,
  FaMoneyBillWave,
  FaGraduationCap,
  FaLayerGroup,
  FaPhoneAlt,
  FaTimes,
  FaDownload,
  FaPaperPlane,
} from "react-icons/fa";
import { toast } from "react-toastify";
import { useReactToPrint } from "react-to-print";
import { useNavigate } from "react-router-dom";
import { classesList } from "../../jsonsFiles/classesList";

export default function StudentFeeProfilePage() {
  const [students, setStudents] = useState([]);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [profileData, setProfileData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [studentsLoading, setStudentsLoading] = useState(true);

  // Filters for student selection
  const [selectedClass, setSelectedClass] = useState("ALL");
  const [selectedSection, setSelectedSection] = useState("ALL");
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [activeTab, setActiveTab] = useState("overview");

  const { token } = useAuth();
  const navigate = useNavigate();
  const printStatementRef = useRef();

  const handlePrintStatement = useReactToPrint({
    contentRef: printStatementRef,
    documentTitle: `Fee_Statement_${selectedStudent?.studentName || "Student"}_${selectedStudent?.admissionNumber || ""}`,
    pageStyle: `@page { size: A4 portrait; margin: 10mm; }`,
  });

  // Debounce search input (300ms)
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearch(searchTerm.trim());
    }, 300);
    return () => clearTimeout(handler);
  }, [searchTerm]);

  // Fetch student directory directly from API with filters
  useEffect(() => {
    const fetchStudents = async () => {
      try {
        setStudentsLoading(true);
        const params = new URLSearchParams();
        if (selectedClass !== "ALL") params.append("class", selectedClass);
        if (selectedSection !== "ALL") params.append("section", selectedSection);
        if (debouncedSearch) params.append("search", debouncedSearch);

        const res = await axios.get(`${server}/students?${params.toString()}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const list = res.data.data || [];
        setStudents(list);

        if (list.length > 0) {
          const exists = list.some((s) => s.id === selectedStudent?.id);
          if (!exists) {
            setSelectedStudent(list[0]);
          }
        } else {
          setSelectedStudent(null);
        }
      } catch (error) {
        toast.error("Failed to load students directory");
      } finally {
        setStudentsLoading(false);
      }
    };
    if (token) fetchStudents();
  }, [token, selectedClass, selectedSection, debouncedSearch]);

  // Fetch financial profile for selected student
  useEffect(() => {
    if (!selectedStudent || !token) return;

    const fetchStudentProfile = async () => {
      try {
        setLoading(true);
        const res = await axios.get(
          `${server}/fee-due/student-profile/${selectedStudent.id}`,
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );
        if (res.data.success) {
          setProfileData(res.data.data);
        }
      } catch (error) {
        toast.error("Failed to load student fee ledger");
      } finally {
        setLoading(false);
      }
    };

    fetchStudentProfile();
  }, [selectedStudent, token]);

  // Calculate Running Balance Ledger
  const ledgerEntries = useMemo(() => {
    if (!profileData) return [];

    const entries = [];

    // Add Assigned Fees as Debits
    (profileData.assignedFees || []).forEach((f) => {
      entries.push({
        id: `fee-${f.id}`,
        date: f.createdAt || f.dueDate,
        type: "BILL",
        description: `${f.feeStructure?.title || "Fee Bill"} (${f.month})`,
        debit: f.totalAmount || 0,
        credit: 0,
        ref: f.month,
      });
    });

    // Add Payments as Credits
    (profileData.payments || []).forEach((p) => {
      entries.push({
        id: `pay-${p.id}`,
        date: p.paymentDate || p.createdAt,
        type: "PAYMENT",
        description: `Fee Payment Received (${p.paymentMode || "Cash"})`,
        debit: 0,
        credit: p.amountPaid ?? p.totalAmountPaid ?? 0,
        ref: p.remarks || p.paymentMode,
      });
    });

    // Sort by Date Ascending
    entries.sort((a, b) => new Date(a.date) - new Date(b.date));

    // Calculate Running Balance
    let balance = 0;
    return entries.map((entry) => {
      balance += entry.debit - entry.credit;
      return { ...entry, runningBalance: balance };
    });
  }, [profileData]);

  // Financial Percent Completion
  const totalBilled = profileData?.overview?.totalAssigned || 0;
  const totalPaid = profileData?.overview?.totalPaid || 0;
  const pendingBalance = profileData?.overview?.totalPending || 0;
  const paymentPercentage =
    totalBilled > 0
      ? Math.min(100, Math.round((totalPaid / totalBilled) * 100))
      : 100;

  return (
    <div className="p-4 max-w-[1400px] mx-auto min-h-screen bg-gray-50 dark:bg-slate-950 text-gray-800 dark:text-slate-100 font-sans transition-colors duration-300">
      {/* Compact Top Header Bar */}
      <div className="bg-white dark:bg-slate-900 border border-gray-200 dark:border-slate-800 rounded-2xl px-5 py-3 mb-4 shadow-xs flex flex-col md:flex-row justify-between items-start md:items-center gap-3">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-indigo-100 dark:bg-indigo-950 text-indigo-600 dark:text-indigo-400 rounded-xl shadow-xs">
            <FaUserGraduate size={18} />
          </div>
          <div>
            <h1 className="text-lg font-black text-gray-900 dark:text-white leading-tight">Student Fee Profile & Ledger</h1>
            <p className="text-xs text-gray-500 dark:text-slate-400 font-semibold">Inspect student financial statements, running ledger, and payment records</p>
          </div>
        </div>

        {selectedStudent && (
          <div className="flex items-center gap-2">
            <button
              onClick={() => navigate("/my/fees/collect")}
              className="bg-emerald-600 hover:bg-emerald-700 text-white px-3.5 py-1.5 rounded-xl flex items-center font-black text-xs transition shadow-sm"
            >
              <FaMoneyBillWave className="mr-1.5" /> Collect Fee
            </button>
            <button
              onClick={handlePrintStatement}
              className="bg-indigo-600 hover:bg-indigo-700 text-white px-3.5 py-1.5 rounded-xl flex items-center font-black text-xs transition shadow-sm"
            >
              <FaPrint className="mr-1.5" /> Print Statement
            </button>
          </div>
        )}
      </div>

      {/* Compact Student Selector Toolbar */}
      <div className="bg-white dark:bg-slate-900 p-3.5 rounded-2xl border border-gray-200 dark:border-slate-800 shadow-xs mb-4 space-y-2.5">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-2">
          <div className="md:col-span-3">
            <select
              value={selectedClass}
              onChange={(e) => setSelectedClass(e.target.value)}
              className="w-full px-3 py-1.5 bg-gray-50 dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-lg text-xs font-bold text-gray-800 dark:text-slate-200 outline-none"
            >
              <option value="ALL">All Classes</option>
              {classesList.map((c, i) => (
                <option key={i} value={c}>
                  Class - {c}
                </option>
              ))}
            </select>
          </div>

          <div className="md:col-span-3">
            <select
              value={selectedSection}
              onChange={(e) => setSelectedSection(e.target.value)}
              className="w-full px-3 py-1.5 bg-gray-50 dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-lg text-xs font-bold text-gray-800 dark:text-slate-200 outline-none"
            >
              <option value="ALL">All Sections</option>
              {["A", "B", "C", "D", "E"].map((sec, i) => (
                <option key={i} value={sec}>
                  Section - {sec}
                </option>
              ))}
            </select>
          </div>

          <div className="md:col-span-6 relative">
            <FaSearch className="absolute left-3 top-2.5 text-gray-400 dark:text-slate-500 text-xs" />
            <input
              type="text"
              placeholder="Search Student Name, Reg No, Roll No..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-8 pr-3 py-1.5 bg-gray-50 dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-lg text-xs font-bold text-gray-800 dark:text-slate-100 outline-none"
            />
          </div>
        </div>

        {/* High-Density Student Selection Pills Carousel */}
        <div className="flex gap-1.5 overflow-x-auto pb-1 custom-scrollbar">
          {studentsLoading ? (
            <span className="text-xs text-gray-400">Loading student directory...</span>
          ) : students.length === 0 ? (
            <span className="text-xs text-gray-400 italic">No matching students</span>
          ) : (
            students.slice(0, 20).map((st) => {
              const isSelected = selectedStudent?.id === st.id;
              return (
                <button
                  key={st.id}
                  onClick={() => setSelectedStudent(st)}
                  className={`px-3 py-1 rounded-xl text-xs font-bold transition whitespace-nowrap flex items-center gap-1.5 border ${
                    isSelected
                      ? "bg-indigo-600 text-white border-indigo-600 shadow-xs"
                      : "bg-gray-50 dark:bg-slate-800 text-gray-700 dark:text-slate-300 border-gray-200 dark:border-slate-700 hover:border-indigo-400"
                  }`}
                >
                  <span
                    className={`w-4 h-4 rounded-full text-[9px] flex items-center justify-center font-black ${
                      isSelected
                        ? "bg-white/20 text-white"
                        : "bg-indigo-100 text-indigo-700 dark:bg-indigo-950 dark:text-indigo-300"
                    }`}
                  >
                    {(st.studentName || "S").charAt(0).toUpperCase()}
                  </span>
                  {st.studentName} (Cl: {st.class}-{st.section})
                </button>
              );
            })
          )}
        </div>
      </div>

      {!selectedStudent ? (
        <div className="bg-white dark:bg-slate-900 rounded-2xl border border-gray-200 dark:border-slate-800 p-12 text-center text-gray-400 dark:text-slate-500 shadow-xs">
          <FaUserGraduate className="text-5xl mx-auto mb-3 text-indigo-500 opacity-50" />
          <h3 className="text-base font-bold text-gray-800 dark:text-white">Select a Student</h3>
          <p className="text-xs text-gray-400 dark:text-slate-500 mt-1">
            Choose a student above to inspect their fee ledger & payment receipts.
          </p>
        </div>
      ) : loading ? (
        <div className="flex justify-center items-center h-48 text-indigo-500 font-bold text-xs">
          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-indigo-600 mr-2"></div>
          Loading student financial ledger...
        </div>
      ) : (
        <div className="space-y-4">
          {/* Compact Student Hero Banner */}
          <div className="bg-gradient-to-r from-indigo-50 via-white to-slate-50 dark:from-slate-900 dark:via-slate-900 dark:to-slate-950 p-5 rounded-2xl border border-indigo-100 dark:border-slate-800 shadow-sm">
            <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-4">
              <div className="flex items-center gap-3.5">
                <div className="w-14 h-14 rounded-2xl bg-indigo-600 text-white font-black flex items-center justify-center text-xl shadow-xs">
                  {(selectedStudent.studentName || "S").charAt(0).toUpperCase()}
                </div>
                <div>
                  <div className="flex items-center gap-2">
                    <h3 className="text-lg font-black text-gray-900 dark:text-white leading-tight">
                      {selectedStudent.studentName}
                    </h3>
                    <span className="bg-indigo-100 text-indigo-800 dark:bg-indigo-950 dark:text-indigo-300 text-[10px] font-black px-2 py-0.5 rounded-md border border-indigo-200 dark:border-indigo-900">
                      Reg: {selectedStudent.admissionNumber || "N/A"}
                    </span>
                  </div>
                  <p className="text-xs text-gray-600 dark:text-slate-400 font-medium mt-0.5">
                    Class {selectedStudent.class} ({selectedStudent.section}) • Roll: {selectedStudent.rollNumber || "N/A"} • Father: <strong>{selectedStudent.fatherName || "N/A"}</strong> ({selectedStudent.fatherMobileNo || "N/A"})
                  </p>
                </div>
              </div>

              {/* Compact Financial KPIs Row */}
              <div className="grid grid-cols-3 gap-2.5 w-full lg:w-auto">
                <div className="bg-white dark:bg-slate-800 p-2.5 rounded-xl border border-gray-200 dark:border-slate-700 text-center">
                  <span className="text-[9px] font-extrabold uppercase text-gray-400 tracking-wider">Total Billed</span>
                  <p className="text-base font-black text-gray-900 dark:text-white mt-0.5">₹{totalBilled.toLocaleString()}</p>
                </div>
                <div className="bg-emerald-50 dark:bg-emerald-950/50 p-2.5 rounded-xl border border-emerald-200 dark:border-emerald-900 text-center">
                  <span className="text-[9px] font-extrabold uppercase text-emerald-700 dark:text-emerald-300 tracking-wider">Total Paid</span>
                  <p className="text-base font-black text-emerald-600 dark:text-emerald-400 mt-0.5">₹{totalPaid.toLocaleString()}</p>
                </div>
                <div className="bg-rose-50 dark:bg-rose-950/50 p-2.5 rounded-xl border border-rose-200 dark:border-rose-900 text-center">
                  <span className="text-[9px] font-extrabold uppercase text-rose-700 dark:text-rose-300 tracking-wider">Outstanding</span>
                  <p className="text-base font-black text-rose-600 dark:text-rose-400 mt-0.5">₹{pendingBalance.toLocaleString()}</p>
                </div>
              </div>
            </div>

            {/* Compact Progress Bar */}
            <div className="mt-3 pt-2 border-t border-gray-200 dark:border-slate-800 flex items-center gap-3">
              <span className="text-xs font-bold text-gray-600 dark:text-slate-400 whitespace-nowrap">Settlement:</span>
              <div className="flex-1 h-2 bg-gray-200 dark:bg-slate-800 rounded-full overflow-hidden">
                <div
                  className="h-full bg-gradient-to-r from-emerald-500 to-teal-500 rounded-full transition-all duration-300"
                  style={{ width: `${paymentPercentage}%` }}
                ></div>
              </div>
              <span className="text-xs font-extrabold text-emerald-600 dark:text-emerald-400">{paymentPercentage}% Paid</span>
            </div>
          </div>

          {/* Compact Navigation Tabs Bar */}
          <div className="flex gap-1.5 border-b border-gray-200 dark:border-slate-800 pb-2 overflow-x-auto">
            {[
              { id: "overview", label: "Overview", icon: FaFileInvoiceDollar },
              { id: "ledger", label: `Running Ledger (${ledgerEntries.length})`, icon: FaHistory },
              { id: "assigned", label: `Assigned Fees (${profileData?.assignedFees?.length || 0})`, icon: FaFileInvoiceDollar },
              { id: "receipts", label: `Receipts (${profileData?.receipts?.length || 0})`, icon: FaReceipt },
              { id: "reminders", label: `Reminders (${profileData?.reminders?.length || 0})`, icon: FaBell },
            ].map((tab) => {
              const Icon = tab.icon;
              const isActive = activeTab === tab.id;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`px-3.5 py-1.5 rounded-xl text-xs font-black transition flex items-center gap-1.5 whitespace-nowrap ${
                    isActive
                      ? "bg-indigo-600 text-white shadow-xs"
                      : "bg-white dark:bg-slate-900 text-gray-600 dark:text-slate-300 border border-gray-200 dark:border-slate-800 hover:bg-gray-100"
                  }`}
                >
                  <Icon size={12} /> {tab.label}
                </button>
              );
            })}
          </div>

          {/* Compact Tab Content Container */}
          <div className="bg-white dark:bg-slate-900 rounded-2xl border border-gray-200 dark:border-slate-800 p-4 shadow-xs">
            {/* OVERVIEW TAB */}
            {activeTab === "overview" && (
              <div className="space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  <div className="p-4 bg-emerald-50 dark:bg-emerald-950/40 border border-emerald-200 dark:border-emerald-900 rounded-xl">
                    <span className="text-[10px] font-bold text-emerald-800 dark:text-emerald-300 uppercase tracking-wider">Collected Amount</span>
                    <div className="text-2xl font-black text-emerald-700 dark:text-emerald-400 mt-1">₹{totalPaid.toLocaleString()}</div>
                  </div>

                  <div className="p-4 bg-rose-50 dark:bg-rose-950/40 border border-rose-200 dark:border-rose-900 rounded-xl">
                    <span className="text-[10px] font-bold text-rose-800 dark:text-rose-300 uppercase tracking-wider">Pending Dues</span>
                    <div className="text-2xl font-black text-rose-700 dark:text-rose-400 mt-1">₹{pendingBalance.toLocaleString()}</div>
                  </div>

                  <div className="p-4 bg-purple-50 dark:bg-purple-950/40 border border-purple-200 dark:border-purple-900 rounded-xl">
                    <span className="text-[10px] font-bold text-purple-800 dark:text-purple-300 uppercase tracking-wider">Receipts Issued</span>
                    <div className="text-2xl font-black text-purple-700 dark:text-purple-400 mt-1">{profileData?.overview?.totalReceipts || 0} Receipts</div>
                  </div>
                </div>

                <div className="border border-gray-200 dark:border-slate-800 rounded-xl p-4">
                  <h4 className="font-extrabold text-xs text-gray-900 dark:text-white mb-3 flex items-center gap-1.5">
                    <FaHistory className="text-indigo-500" /> Recent Ledger Transactions
                  </h4>

                  <div className="space-y-1.5">
                    {ledgerEntries.length === 0 ? (
                      <p className="text-xs text-gray-400 italic">No ledger transactions recorded yet.</p>
                    ) : (
                      ledgerEntries.slice(-5).reverse().map((entry, idx) => (
                        <div key={idx} className="p-2 bg-gray-50 dark:bg-slate-800/60 rounded-lg flex justify-between items-center text-xs">
                          <div className="flex items-center gap-2">
                            <span className={`px-2 py-0.5 rounded text-[10px] font-black ${entry.type === "BILL" ? "bg-rose-100 text-rose-700 dark:bg-rose-950 dark:text-rose-400" : "bg-emerald-100 text-emerald-700 dark:bg-emerald-950 dark:text-emerald-400"}`}>
                              {entry.type}
                            </span>
                            <span className="font-bold text-gray-800 dark:text-slate-200">{entry.description}</span>
                          </div>
                          <span className={`font-black ${entry.type === "BILL" ? "text-rose-600 dark:text-rose-400" : "text-emerald-600 dark:text-emerald-400"}`}>
                            {entry.debit > 0 ? `+₹${(entry.debit || 0).toLocaleString()}` : `-₹${(entry.credit || 0).toLocaleString()}`}
                          </span>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* RUNNING LEDGER TAB */}
            {activeTab === "ledger" && (
              <div className="overflow-x-auto">
                <table className="w-full text-left border-collapse text-xs">
                  <thead>
                    <tr className="bg-gray-50 dark:bg-slate-800 border-b border-gray-200 dark:border-slate-700 text-gray-500 dark:text-slate-400 font-black uppercase">
                      <th className="p-2.5">Date</th>
                      <th className="p-2.5">Type</th>
                      <th className="p-2.5">Description</th>
                      <th className="p-2.5 text-right">Billed (Debit)</th>
                      <th className="p-2.5 text-right">Paid (Credit)</th>
                      <th className="p-2.5 text-right">Running Balance</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100 dark:divide-slate-800">
                    {ledgerEntries.map((row, idx) => (
                      <tr key={idx} className="hover:bg-gray-50 dark:hover:bg-slate-800/40">
                        <td className="p-2.5 font-semibold text-gray-500">{new Date(row.date).toLocaleDateString()}</td>
                        <td className="p-2.5">
                          <span className={`px-2 py-0.5 rounded text-[10px] font-black ${row.type === "BILL" ? "bg-rose-100 text-rose-700 dark:bg-rose-950 dark:text-rose-400" : "bg-emerald-100 text-emerald-700 dark:bg-emerald-950 dark:text-emerald-400"}`}>
                            {row.type}
                          </span>
                        </td>
                        <td className="p-2.5 font-bold text-gray-800 dark:text-slate-200">{row.description}</td>
                        <td className="p-2.5 text-right font-black text-rose-600 dark:text-rose-400">{row.debit > 0 ? `₹${(row.debit || 0).toLocaleString()}` : "-"}</td>
                        <td className="p-2.5 text-right font-black text-emerald-600 dark:text-emerald-400">{row.credit > 0 ? `₹${(row.credit || 0).toLocaleString()}` : "-"}</td>
                        <td className="p-2.5 text-right font-black text-indigo-600 dark:text-indigo-400">₹{(row.runningBalance || 0).toLocaleString()}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {/* ASSIGNED FEES TAB */}
            {activeTab === "assigned" && (
              <div className="overflow-x-auto">
                <table className="w-full text-left border-collapse text-xs">
                  <thead>
                    <tr className="bg-gray-50 dark:bg-slate-800 border-b border-gray-200 dark:border-slate-700 text-gray-500 dark:text-slate-400 font-black uppercase">
                      <th className="p-2.5">Month</th>
                      <th className="p-2.5">Fee Title</th>
                      <th className="p-2.5">Due Date</th>
                      <th className="p-2.5 text-right">Total Amount</th>
                      <th className="p-2.5 text-right">Paid Amount</th>
                      <th className="p-2.5 text-center">Status</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100 dark:divide-slate-800">
                    {(profileData?.assignedFees || []).map((f) => (
                      <tr key={f.id} className="hover:bg-gray-50 dark:hover:bg-slate-800/40">
                        <td className="p-2.5 font-black text-gray-900 dark:text-white">{f.month}</td>
                        <td className="p-2.5 font-bold text-gray-700 dark:text-slate-300">{f.feeStructure?.title || "Tuition Fee"}</td>
                        <td className="p-2.5 font-medium text-gray-500">{new Date(f.dueDate).toLocaleDateString()}</td>
                        <td className="p-2.5 text-right font-black text-gray-900 dark:text-white">₹{(f.totalAmount || 0).toLocaleString()}</td>
                        <td className="p-2.5 text-right font-black text-emerald-600 dark:text-emerald-400">₹{(f.paidAmount || 0).toLocaleString()}</td>
                        <td className="p-2.5 text-center">
                          <span className={`px-2 py-0.5 rounded text-[10px] font-black uppercase ${f.status === "Paid" ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-950 dark:text-emerald-400" : "bg-rose-100 text-rose-700 dark:bg-rose-950 dark:text-rose-400"}`}>
                            {f.status}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {/* RECEIPTS TAB */}
            {activeTab === "receipts" && (
              <div className="overflow-x-auto">
                <table className="w-full text-left border-collapse text-xs">
                  <thead>
                    <tr className="bg-gray-50 dark:bg-slate-800 border-b border-gray-200 dark:border-slate-700 text-gray-500 dark:text-slate-400 font-black uppercase">
                      <th className="p-2.5">Receipt No</th>
                      <th className="p-2.5">Date</th>
                      <th className="p-2.5">Payment Mode</th>
                      <th className="p-2.5 text-right">Amount Paid</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100 dark:divide-slate-800">
                    {(profileData?.receipts || []).map((r) => {
                      const amount = r.amountPaid ?? r.totalAmountPaid ?? 0;
                      const dateVal = r.collectionDate || r.receiptDate || r.createdAt;
                      return (
                        <tr key={r.id} className="hover:bg-gray-50 dark:hover:bg-slate-800/40">
                          <td className="p-2.5 font-black text-indigo-600 dark:text-indigo-400">{r.receiptNumber}</td>
                          <td className="p-2.5 font-medium text-gray-500">{dateVal ? new Date(dateVal).toLocaleDateString() : "-"}</td>
                          <td className="p-2.5 font-bold text-gray-700 dark:text-slate-300">{r.paymentMode || "Cash"}</td>
                          <td className="p-2.5 text-right font-black text-emerald-600 dark:text-emerald-400">
                            ₹{amount.toLocaleString()}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}

            {/* REMINDERS TAB */}
            {activeTab === "reminders" && (
              <div className="space-y-2">
                {(profileData?.reminders || []).length === 0 ? (
                  <p className="text-xs text-gray-400 italic">No reminders sent to this student yet.</p>
                ) : (
                  (profileData?.reminders || []).map((rem) => (
                    <div key={rem.id} className="p-3 bg-gray-50 dark:bg-slate-800/60 rounded-xl flex justify-between items-center text-xs">
                      <div>
                        <span className="font-bold text-gray-800 dark:text-slate-200">{rem.channel} Reminder</span>
                        <p className="text-gray-500 text-[10px] mt-0.5">{rem.message}</p>
                      </div>
                      <span className="text-gray-400 text-[10px] font-semibold">{new Date(rem.sentAt).toLocaleDateString()}</span>
                    </div>
                  ))
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Hidden Printable Fee Statement Document */}
      <div className="hidden">
        <div ref={printStatementRef} className="p-8 bg-white text-gray-900 font-sans w-[210mm] min-h-[297mm]">
          <div className="flex justify-between items-center border-b-2 border-indigo-900 pb-4 mb-6">
            <div className="flex items-center gap-4">
              <img src="/assets/images/favIcon.png" alt="Logo" className="w-14 h-14 object-contain" />
              <div>
                <h1 className="text-xl font-black uppercase text-indigo-950">DEE NOVO PUBLIC SCHOOL</h1>
                <p className="text-xs text-gray-600 font-semibold">Student Account Fee Statement & Ledger</p>
                <p className="text-xs text-gray-500">Date Generated: {new Date().toLocaleDateString()}</p>
              </div>
            </div>
            <div className="text-right">
              <div className="text-lg font-black text-indigo-900">STUDENT FEE STATEMENT</div>
              <p className="text-xs text-gray-500">Reg: {selectedStudent?.admissionNumber}</p>
            </div>
          </div>

          {/* Student Profile Info */}
          <div className="bg-gray-100 p-4 rounded-xl mb-6 grid grid-cols-2 gap-2 text-xs">
            <div><strong>Student Name:</strong> {selectedStudent?.studentName}</div>
            <div><strong>Class & Section:</strong> Class {selectedStudent?.class} ({selectedStudent?.section})</div>
            <div><strong>Roll Number:</strong> {selectedStudent?.rollNumber || "N/A"}</div>
            <div><strong>Father Name:</strong> {selectedStudent?.fatherName || "N/A"}</div>
          </div>

          {/* Ledger Table */}
          <table className="w-full text-left border-collapse text-xs mb-6">
            <thead>
              <tr className="bg-indigo-900 text-white uppercase font-bold">
                <th className="p-2">Date</th>
                <th className="p-2">Type</th>
                <th className="p-2">Description</th>
                <th className="p-2 text-right">Debit (Billed)</th>
                <th className="p-2 text-right">Credit (Paid)</th>
                <th className="p-2 text-right">Balance</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-300">
              {ledgerEntries.map((row, idx) => (
                <tr key={idx}>
                  <td className="p-2">{new Date(row.date).toLocaleDateString()}</td>
                  <td className="p-2 font-bold">{row.type}</td>
                  <td className="p-2 font-semibold">{row.description}</td>
                  <td className="p-2 text-right">{row.debit > 0 ? `₹${row.debit}` : "-"}</td>
                  <td className="p-2 text-right text-emerald-700">{row.credit > 0 ? `₹${row.credit}` : "-"}</td>
                  <td className="p-2 text-right font-bold text-indigo-900">₹{row.runningBalance}</td>
                </tr>
              ))}
            </tbody>
          </table>

          {/* Summary */}
          <div className="flex justify-between items-center border-t-2 border-indigo-900 pt-4 text-xs font-bold">
            <div>Total Billed: ₹{totalBilled.toLocaleString()} | Total Paid: ₹{totalPaid.toLocaleString()}</div>
            <div className="text-sm font-black text-indigo-950">Net Outstanding: ₹{pendingBalance.toLocaleString()}</div>
          </div>
        </div>
      </div>
    </div>
  );
}
